import {
    type AfterViewInit,
    Component,
    type ElementRef,
    type OnInit,
    ViewChild,
    inject,
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

import type { NgbModalRef } from "@ng-bootstrap/ng-bootstrap";

import { PasswordResetFinishService } from "./password-reset-finish.service";

@Component({
    selector: "jhi-password-reset-finish",
    templateUrl: "./password-reset-finish.component.html",
    standalone: false,
})
export class PasswordResetFinishComponent implements OnInit, AfterViewInit {
    private passwordResetFinishService = inject(PasswordResetFinishService);
    private route = inject(ActivatedRoute);
    private fb = inject(UntypedFormBuilder);
    private router = inject(Router);

    private static readonly INVALID_KEY_FALLBACK_MESSAGE =
        "This password reset key is invalid or has expired. Please request a new password reset if needed.";
    private static readonly GENERIC_ERROR_MESSAGE =
        "Uh oh! Something went wrong. Please try again in a couple minutes.";
    private static readonly INVALID_PASSWORD_TYPE_SUFFIX = "/invalid-password";

    doNotMatch: string;
    error: string;
    keyMissing: boolean;
    invalidKey: boolean;
    invalidKeyMessage: string;
    modalRef: NgbModalRef;
    key: string;
    accountActivation = false;

    passwordForm = this.fb.group({
        newPassword: [
            "",
            [
                Validators.required,
                Validators.minLength(8),
                Validators.maxLength(100),
                Validators.pattern(".*[A-Z]+.*"),
                Validators.pattern(".*[a-z]+.*"),
                Validators.pattern(".*[0-9]+.*"),
                Validators.pattern(".*[!@#$/?%^&*=+;:,.<>()_-]+.*"),
            ],
        ],
        confirmPassword: [
            "",
            [
                Validators.required,
                Validators.minLength(8),
                Validators.maxLength(100),
            ],
        ],
    });

    @ViewChild("passwordField") public passwordField: ElementRef;

    ngOnInit(): void {
        this.key = this.route.snapshot.queryParamMap.get("key");
        this.accountActivation =
            !!this.route.snapshot.queryParamMap.get("activate");
        this.keyMissing = !this.key;

        if (!this.keyMissing) {
            this.passwordResetFinishService.checkKey(this.key).subscribe({
                next: () => {
                    // success...let them reset
                },
                error: (error) => {
                    const code = error?.error?.code;
                    const detail = error?.error?.detail;
                    if (code === "RESET_KEY_INVALID") {
                        this.invalidKey = true;
                        this.invalidKeyMessage =
                            detail ||
                            PasswordResetFinishComponent.INVALID_KEY_FALLBACK_MESSAGE;
                    } else {
                        this.error =
                            PasswordResetFinishComponent.GENERIC_ERROR_MESSAGE;
                    }
                },
            });
        }
    }

    ngAfterViewInit(): void {
        if (this.passwordField) {
            this.passwordField.nativeElement.focus();
        }
    }

    finishReset(): void {
        this.doNotMatch = null;
        this.error = null;
        const password = this.passwordForm.get(["newPassword"]).value;
        const confirmPassword = this.passwordForm.get([
            "confirmPassword",
        ]).value;
        if (password !== confirmPassword) {
            this.doNotMatch = "ERROR";
        } else {
            this.passwordResetFinishService
                .save({ key: this.key, newPassword: password })
                .subscribe({
                    next: () => {
                        this.router.navigate(["/login"], {
                            queryParams: {
                                source: this.accountActivation
                                    ? "activate"
                                    : "reset",
                            },
                        });
                    },
                    error: (error) => {
                        const code = error?.error?.code;
                        const detail = error?.error?.detail;
                        const type = error?.error?.type;
                        if (code === "RESET_KEY_INVALID") {
                            this.invalidKey = true;
                            this.invalidKeyMessage =
                                detail ||
                                PasswordResetFinishComponent.INVALID_KEY_FALLBACK_MESSAGE;
                            return;
                        }
                        if (
                            type?.endsWith(
                                PasswordResetFinishComponent.INVALID_PASSWORD_TYPE_SUFFIX,
                            ) &&
                            detail
                        ) {
                            this.error = detail;
                            return;
                        }
                        this.error =
                            PasswordResetFinishComponent.GENERIC_ERROR_MESSAGE;
                    },
                });
        }
    }
}
